import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMenu, createOrder } from '../../services/api';
import { addOrderToQueue } from '../../services/offlineSync'; // Importar a função de sincronização

function OrderScreen() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // { itemId: { item, quantity, observation } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const data = await getMenu();
        setMenuItems(data);
      } catch (err) {
        console.error('Failed to fetch menu:', err);
        setError('Não foi possível carregar o cardápio.');
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  const handleAddItem = (item) => {
    setSelectedItems((prev) => {
      const newQuantity = (prev[item.ItemID]?.quantity || 0) + 1;
      return {
        ...prev,
        [item.ItemID]: {
          item,
          quantity: newQuantity,
          observation: prev[item.ItemID]?.observation || '',
        },
      };
    });
  };

  const handleRemoveItem = (itemId) => {
    setSelectedItems((prev) => {
      const newQuantity = (prev[itemId]?.quantity || 0) - 1;
      if (newQuantity <= 0) {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      }
      return {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          quantity: newQuantity,
        },
      };
    });
  };

  const handleObservationChange = (itemId, observation) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        observation,
      },
    }));
  };

  const calculateTotal = () => {
    return Object.values(selectedItems).reduce(
      (total, { item, quantity }) => total + item.Price * quantity,
      0
    );
  };

  const handleSubmitOrder = async () => {
    setError('');
    setOrderSuccess(false);
    const orderItems = Object.values(selectedItems).map(({ item, quantity, observation }) => ({
      item_id: item.ItemID,
      quantity,
      observation,
    }));

    // TODO: user_id e RestaurantID devem vir do token de autenticação
    const userId = 1; // Placeholder
    const restaurantId = "a1b2c3d4-e5f6-7890-1234-567890abcdef"; // Placeholder

    const orderData = {
      table_id: parseInt(tableId),
      user_id: userId,
      items: orderItems,
      RestaurantID: restaurantId,
    };

    try {
      await createOrder(orderData);
      setOrderSuccess(true);
      setSelectedItems({}); // Limpa os itens selecionados após o sucesso
      alert('Pedido enviado com sucesso!');
      navigate('/waiter/tables'); // Volta para seleção de mesas
    } catch (err) {
      console.error('Failed to create order:', err);
      // Verifica se o erro é de rede (offline)
      if (!navigator.onLine) {
        addOrderToQueue(orderData);
        alert('Você está offline. Pedido salvo para sincronização futura.');
        navigate('/waiter/tables'); // Volta para seleção de mesas
      } else {
        setError('Falha ao enviar pedido. Tente novamente.');
      }
    }
  };

  if (loading) {
    return <div style={styles.loading}>Carregando cardápio...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Mesa {tableId} - Novo Pedido</h2>

      <div style={styles.section}>
        <h3>Cardápio</h3>
        <div style={styles.menuGrid}>
          {menuItems.map((item) => (
            <div key={item.ItemID} style={styles.menuItemCard}>
              <h4>{item.Name}</h4>
              <p>R$ {item.Price.toFixed(2)}</p>
              <button onClick={() => handleAddItem(item)} style={styles.addButton}>Adicionar</button>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.section}>
        <h3>Itens do Pedido</h3>
        {Object.keys(selectedItems).length === 0 ? (
          <p>Nenhum item selecionado.</p>
        ) : (
          <ul style={styles.selectedItemsList}>
            {Object.values(selectedItems).map(({ item, quantity, observation }) => (
              <li key={item.ItemID} style={styles.selectedItem}>
                <span>
                  {item.Name} (x{quantity}) - R$ {(item.Price * quantity).toFixed(2)}
                </span>
                <div style={styles.itemControls}>
                  <button onClick={() => handleRemoveItem(item.ItemID)} style={styles.controlButton}>-</button>
                  <button onClick={() => handleAddItem(item)} style={styles.controlButton}>+</button>
                  <input
                    type="text"
                    placeholder="Observação"
                    value={observation}
                    onChange={(e) => handleObservationChange(item.ItemID, e.target.value)}
                    style={styles.observationInput}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
        <h3 style={styles.total}>Total: R$ {calculateTotal().toFixed(2)}</h3>
        <button
          onClick={handleSubmitOrder}
          style={styles.submitButton}
          disabled={Object.keys(selectedItems).length === 0}
        >
          Enviar Pedido
        </button>
        {error && <p style={styles.error}>{error}</p>}
        {orderSuccess && <p style={styles.success}>Pedido enviado com sucesso!</p>}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f0f2f5',
    minHeight: '100vh',
    fontFamily: 'Arial, sans-serif',
  },
  title: {
    color: '#333',
    marginBottom: '30px',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '900px',
  },
  menuGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '15px',
  },
  menuItemCard: {
    border: '1px solid #eee',
    borderRadius: '6px',
    padding: '10px',
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
  },
  addButton: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  selectedItemsList: {
    listStyle: 'none',
    padding: 0,
  },
  selectedItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #eee',
  },
  itemControls: {
    display: 'flex',
    alignItems: 'center',
  },
  controlButton: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer',
    margin: '0 5px',
  },
  observationInput: {
    padding: '5px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    marginLeft: '10px',
  },
  total: {
    textAlign: 'right',
    marginTop: '20px',
    fontSize: '20px',
    color: '#333',
  },
  submitButton: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '12px 20px',
    fontSize: '18px',
    cursor: 'pointer',
    width: '100%',
    marginTop: '20px',
  },
  loading: {
    fontSize: '18px',
    color: '#555',
    marginTop: '50px',
  },
  error: {
    color: 'red',
    marginTop: '10px',
    textAlign: 'center',
  },
  success: {
    color: 'green',
    marginTop: '10px',
    textAlign: 'center',
  },
};

export default OrderScreen;
